import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { addDays, startOfMonth, endOfMonth, format } from "date-fns";
import { es } from "date-fns/locale";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Reservation } from "../../reservations/types/Reservation";
import { useMonthReservations } from "../hooks/useMonthReservations";
import { useMonthEstacionamientoReservations } from "../hooks/useMonthEstacionamientoReservations";
import { useZonas } from "../hooks/useZonas";
import { useZoneFilter } from "../contexts/ZoneFilterContext";
import { useFilteredDepartments } from "../hooks/useFilteredDepartments";
import { useEstacionamientos } from "../../../hooks/useEstacionamientos";
import { useFilteredEstacionamientos } from "../hooks/useFilteredEstacionamientos";
import CalendarHeader from "./CalendarHeader";
import CalendarGrid from "./CalendarGrid";
import ReservationDetails from "../../reservations/components/ReservationDetails";
import EstacionamientoDetails from "../../reservations/components/EstacionamientoDetails";
import RestrictionModal from "../../auth/components/RestrictionModal";
import FloatingActionButton from "./FloatingActionButton";
import { isReservaEstacionamiento } from "./EstacionamientoRow";
import { ReservaEstacionamientoDisplay } from "../../../types/ReservaEstacionamiento";
import { usePermissions } from "../../auth/hooks/usePermissions";
import { useEditRestriction } from "../../auth/hooks/useEditRestriction";
import { logger } from "../../../utils/logger";
import { useAppVisibility } from "../../shared/hooks/useAppVisibility";
import { Estacionamiento } from "../../../types/Estacionamiento";
import {
  CLEANING_LABELS,
  CLEANING_NAMES,
} from "../utils/cleaningCalendarStyles";
import {
  LazyEditEstacionamientoModal,
  LazyEditReservationModal,
} from "../../shared/components/LazyModals";
import { useCleaningSpecialDaysReason } from "../../cleaning/cleaningSpecialDays";
import { fetchCleaningCalendarRecords } from "../../cleaning/cleaningStaffApi";
import { formatDateKey } from "../../shared/utils/dateUtils";
import websocketService from "../../../services/websocket";
import { DEPARTMENT_UNAVAILABILITY_KEY, useDepartmentUnavailability } from "../departmentAvailability";
import { useFueraDeServicio } from "../hooks/useFueraDeServicio";

const FOREGROUND_REFRESH_AFTER_MS = 60 * 1000;

const Calendar: React.FC = () => {
  const theme = useTheme();
  const { getMotivoDiaSinPersonal } = useCleaningSpecialDaysReason();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [editingReservation, setEditingReservation] = useState(false);
  const [nuevaReservaData, setNuevaReservaData] = useState<{
    entrada: Date;
    departamento?: string;
    estacionamiento?: string;
    salida?: Date;
  } | null>(null);
  // Estados para estacionamiento
  const [selectedEstacionamiento, setSelectedEstacionamiento] =
    useState<ReservaEstacionamientoDisplay | null>(null);

  // Modal unificado - no necesitamos estado separado para estacionamiento
  const [editingEstacionamiento, setEditingEstacionamiento] =
    useState<ReservaEstacionamientoDisplay | null>(null);
  const {
    zonas,
    loading: loadingZonas,
    error: errorZonas,
    reloadZonas,
  } = useZonas();
  const { zoneFilter } = useZoneFilter();
  const {
    data: estacionamientos,
    isLoading: loadingEstacionamientos,
    error: errorEstacionamientos,
  } = useEstacionamientos();
  const permissions = usePermissions();

  // Hook para manejar restricciones de edición
  const {
    isRestrictionModalOpen,
    handleEditAttempt,
    closeRestrictionModal,
    restrictionMessage,
  } = useEditRestriction();

  // Obtener departamentos filtrados por zona (MOVIDO ARRIBA)
  const departamentos = useFilteredDepartments({
    zonas,
    zoneFilter,
    loadingZonas,
    errorZonas,
  });

  const {
    reservations,
    reloadReservations,
  } = useMonthReservations(selectedDate);
  const {
    data: reservasEstacionamiento,
    refetch: reloadReservasEstacionamiento,
  } = useMonthEstacionamientoReservations(
    selectedDate.getFullYear(),
    selectedDate.getMonth()
  );

  // Calcular días del calendario
  const diasDelCalendario = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return Array.from(
      { length: end.getDate() },
      (_, i) => new Date(start.getFullYear(), start.getMonth(), i + 1)
    );
  }, [selectedDate]);
  const fechaReferenciaLimpieza = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const limpiezaDesde = diasDelCalendario[0] ? formatDateKey(diasDelCalendario[0]) : "";
  const limpiezaHasta = diasDelCalendario.length
    ? formatDateKey(diasDelCalendario[diasDelCalendario.length - 1])
    : "";
  const availabilityHasta = diasDelCalendario.length
    ? formatDateKey(addDays(diasDelCalendario[diasDelCalendario.length - 1], 1))
    : "";
  const queryClient = useQueryClient();
  const {
    data: availabilityBlocks = [],
    refetch: reloadAvailabilityBlocks,
  } = useDepartmentUnavailability(limpiezaDesde, availabilityHasta);
  // Por prefijo: también alcanza al formulario de nueva reserva, que consulta con otras fechas.
  const invalidateAvailabilityBlocks = useCallback(
    () => queryClient.invalidateQueries({ queryKey: DEPARTMENT_UNAVAILABILITY_KEY }),
    [queryClient]
  );
  const { data: cleaningRecordsData, refetch: reloadCleaningRecords } = useQuery({
    queryKey: ["cleaning-calendar-records", limpiezaDesde, limpiezaHasta],
    queryFn: () => fetchCleaningCalendarRecords(limpiezaDesde, limpiezaHasta),
    enabled: Boolean(limpiezaDesde && limpiezaHasta),
    staleTime: 60_000,
  });

  // Cuando el personal marca una limpieza, el marcador cambia de estado sin recargar.
  // El evento solo trae entidad y acción (sin datos de huéspedes); los registros se
  // piden por REST, que valida el rol.
  useEffect(() => {
    return websocketService.onActivityLogCreated((payload) => {
      if (payload?.entidad === "limpieza") reloadCleaningRecords();
      if (payload?.entidad === "departamento") invalidateAvailabilityBlocks();
    });
  }, [invalidateAvailabilityBlocks, reloadCleaningRecords]);

  const fueraDeServicio = useFueraDeServicio({ zonas, selectedDate });

  // Obtener estacionamientos filtrados
  const estacionamientosFiltrados = useFilteredEstacionamientos({
    estacionamientos: (estacionamientos as Estacionamiento[]) || [],
    loading: loadingEstacionamientos,
    error: errorEstacionamientos?.message || null,
  });

  const emptyReservationMessage = useMemo(() => {
    const monthLabel = format(selectedDate, "MMMM yyyy", { locale: es });
    if (zoneFilter === "centro") return `No hay reservas en Centro para ${monthLabel}`;
    if (zoneFilter === "loma") return `No hay reservas en Loma para ${monthLabel}`;
    if (zoneFilter === "interno") return `No hay reservas internas para ${monthLabel}`;
    return `No hay reservas para ${monthLabel}`;
  }, [selectedDate, zoneFilter]);

  const handleReservationClick = useCallback(
    (reserva: Reservation | ReservaEstacionamientoDisplay) => {
      if (isReservaEstacionamiento(reserva)) {
        // Abre el modal de detalles de estacionamiento
        setSelectedEstacionamiento(reserva); // 3. USAR EL NUEVO ESTADO
        setEditingEstacionamiento(null);
        setSelectedReservation(null);
      } else {
        setSelectedReservation(reserva);
        // setEditingReservation(true); // <-- ESTO ES LO QUE ABRE EL MODAL DE EDICION
        setEditingEstacionamiento(null);
        setSelectedEstacionamiento(null);
      }
    },
    []
  );

  const handleCloseDetails = () => {
    setSelectedReservation(null);
    setSelectedEstacionamiento(null); // Limpiar ambos
  };

  // 4. NUEVA FUNCIÓN PARA INICIAR EDICIÓN DE ESTACIONAMIENTO
  const handleEditEstacionamiento = (
    reserva: ReservaEstacionamientoDisplay
  ) => {
    const canEdit = handleEditAttempt();
    if (canEdit) {
      setEditingEstacionamiento(reserva);
    }
  };

  const handleEditReservation = () => {
    const canEdit = handleEditAttempt();
    if (canEdit) {
      setEditingReservation(true);
    }
    // Si no puede editar, el modal se abrirá automáticamente
  };

  const handleCloseEdit = () => {
    setEditingReservation(false);
    setSelectedReservation(null);
    setEditingEstacionamiento(null); // Asegurarse de limpiar ambos
    setSelectedEstacionamiento(null);
  };

  const handleReservationUpdated = () => {
    setEditingReservation(false);
    setSelectedReservation(null);
  };

  // Funciones removidas - usando modal unificado

  const handleCellClick = useCallback(
    (fecha: Date, departamento?: string, estacionamiento?: string) => {
      setSelectedReservation(null);
      const fechaSalida = new Date(fecha);
      fechaSalida.setDate(fechaSalida.getDate() + 1);

      const nuevaData = {
        entrada: fecha,
        departamento,
        estacionamiento,
        salida: fechaSalida,
      };

      // Usar el modal unificado para ambos casos
      setNuevaReservaData(nuevaData);
    },
    []
  );

  const handleForeground = useCallback(
    ({ timeInBackgroundMs }: { timeInBackgroundMs: number }) => {
      if (timeInBackgroundMs < FOREGROUND_REFRESH_AFTER_MS) {
        logger.debug("📱 [CALENDAR] App volvió rápido; se conserva la grilla", {
          timeInBackgroundMs,
        });
        return;
      }

      const websocketConnected = websocketService.isConnected();
      logger.info(
        "📱 [CALENDAR] App volvió tras pausa larga; refrescando datos sin desmontar filas",
        { timeInBackgroundMs, websocketConnected }
      );

      reloadZonas({ silent: true });
      if (!websocketConnected) {
        reloadReservations();
      }
      reloadReservasEstacionamiento();
      reloadCleaningRecords();
      reloadAvailabilityBlocks();
    },
    [reloadAvailabilityBlocks, reloadCleaningRecords, reloadReservasEstacionamiento, reloadReservations, reloadZonas]
  );

  // 🔄 MANEJO DE APP EN SEGUNDO PLANO
  useAppVisibility({ onForeground: handleForeground });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <CalendarHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onMarcarFueraDeServicio={permissions.isAdmin ? () => fueraDeServicio.marcar() : undefined}
      />
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { type: "checkout" as const },
            { type: "intermedia" as const },
            { type: "manual" as const },
          ].map((item) => (
            <Box key={item.type} sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.72rem", color: "text.secondary" }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: theme.palette.app.cleaning[item.type],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {CLEANING_LABELS[item.type]}
              </Box>
              {CLEANING_NAMES[item.type]}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          position: "relative",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <CalendarGrid
          diasDelCalendario={diasDelCalendario}
          reservations={reservations || []}
          reservasEstacionamiento={reservasEstacionamiento || []}
          onReservationClick={handleReservationClick}
          departamentos={departamentos}
          estacionamientos={estacionamientosFiltrados.map((e) => e.nombre)}
          selectedDate={selectedDate}
          onCellClick={handleCellClick}
          emptyMessage={emptyReservationMessage}
          getMotivoDiaSinPersonal={getMotivoDiaSinPersonal}
          fechaReferenciaLimpieza={fechaReferenciaLimpieza}
          registrosLimpieza={cleaningRecordsData?.registros || []}
          availabilityBlocks={availabilityBlocks}
          canManageAvailability={permissions.isAdmin}
          onBlockCell={fueraDeServicio.marcar}
          onEditAvailability={fueraDeServicio.editar}
          onDeleteAvailability={fueraDeServicio.pedirQuitar}
        />
      </Box>

      {selectedReservation && (
        <ReservationDetails
          reservation={selectedReservation}
          onClose={handleCloseDetails}
          onEdit={handleEditReservation}
          onDeleted={() => setSelectedReservation(null)}
          permissions={permissions}
        />
      )}

      {/* 5. RENDERIZAR EL NUEVO MODAL DE DETALLES */}
      {selectedEstacionamiento && (
        <EstacionamientoDetails
          reserva={selectedEstacionamiento}
          onClose={handleCloseDetails}
          onEdit={handleEditEstacionamiento}
          permissions={permissions}
        />
      )}

      <FloatingActionButton
        onSuccess={() => {
          setNuevaReservaData(null);
        }}
        onClose={() => {
          setNuevaReservaData(null);
        }}
        existingReservations={reservations}
        initialData={nuevaReservaData as Partial<Reservation>}
        soloEstacionamiento={
          !!nuevaReservaData?.estacionamiento && !nuevaReservaData?.departamento
        }
      />

      {fueraDeServicio.elementos}

      {editingReservation && selectedReservation && (
        <LazyEditReservationModal
          open={true}
          selectedReservation={selectedReservation}
          existingReservations={reservations}
          onClose={handleCloseEdit}
          onSuccess={handleReservationUpdated}
        />
      )}

      {editingEstacionamiento && (
        <LazyEditEstacionamientoModal
          open={true}
          reserva={editingEstacionamiento}
          onClose={handleCloseEdit}
        />
      )}

      {/* Modal unificado maneja tanto departamentos como estacionamientos */}

      {/* Modal de restricción de edición */}
      <RestrictionModal
        open={isRestrictionModalOpen}
        onClose={closeRestrictionModal}
        message={restrictionMessage}
        contactPerson="Mariela Mendes Nunes"
      />
    </Box>
  );
};

export default Calendar;
